<template>
  <div
    class="col column full-height full-width"
    style="background: rgb(239 234 226 / 50%)"
  >
    <div
      class="col column full-height full-width no-wrap"
      id="drop-area"
      @dragover="handleDragOver"
      @drop="handleDrop"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      style="background-image: url('/icons/chat-bg.png'); background-repeat: repeat;"
    >
      <AttendanceHeader
        :id="contactNumber"
        :queue-name="queueName"
        :contact="contactName"
        :direction="attendance.direction"
        :created-at="attendance.answerAt || attendance.createdAt"
        :finished-at="attendance.finishedAt"
        :channel="attendance.channel"
        :show-button-finish-chat="allowAnswer"
        :attendance="attendance"
        :disable-when-pause="isPaused"
        :destination="destination"
        :disable-transfer="isDisable"
        :disable-finish="isFinishDisabled"
        @on-finish="handleFinish"
        @change-view="$emit('change-view', $event)"
        @change-destination="destination = $event"
      />

      <BarAttendanceFrequency
        v-if="isOngoingTab && attendance.direction === 'IN'"
        :attendance-id="attendance.id"
      />

      <ContactAttendancesHistory
        v-show="isHistoryTab"
        :attendance="attendance"
      />

      <template v-if="isOngoingTab">
        <div
          ref="container"
          class="col q-ml-md tw-min-h-0 tw-overflow-hidden"
          sytle="position: relative;"
        >
          <div
            v-if="isLoading"
            class="row full-height items-center"
          >
            <div class="col-auto q-mx-auto q-my-auto">
              <q-spinner-ios
                color="primary"
                size="3em"
              />
            </div>
          </div>

          <ScrollToBottomButton
            ref="scrollBtn"
            @click="scrollToBottom"
          />
          <div
            v-if="isLoadingMessages"
            class="row justify-center q-py-sm"
          >
            <q-spinner
              color="primary"
              size="2em"
            />
          </div>

          <q-virtual-scroll
            ref="scrollmsgs"
            :style="`height: ${heightArea}px; width: ${widthArea}px; display: ${displayArea}`"
            class="q-pr-md q-py-lg"
            :items="messages"
            @virtual-scroll="onScroll"
          >
            <template #default="{ item }">
              <AttendanceChatNote
                v-if="item.type === aiNoteType"
                :key="item.id"
                :note="item"
              />
              <AttendanceChatMessage
                v-else
                :key="item.id || item.temporaryId"
                :msg="item"
                :contact-name="contactName"
                @handlevisibility="handleVisible"
                @reply-to-message="handleReply"
                @scroll-to-parent="handleScrollToParent"
                :is-highlighted="highlightedMessageId === (item.id || item.temporaryId)"
                :is-chat-active="allowAnswer && !isDisable"
                :is-whats-app="isWhatsApp"
              />
            </template>
          </q-virtual-scroll>
        </div>

        <div
          class="col-auto q-px-sm tw-border-0 tw-border-t tw-border-solid tw-border-black/5 tw-bg-white"
          v-if="allowAnswer"
        >
          <template v-if="expiredAttendance">
            <AttendanceChatExpired :attendance="attendance" />

            <AttendanceTransferInput @on-command="handleCommand" />
          </template>

          <template v-else>
            <AttendanceChatInput
              ref="chatInput"
              :input-disable="isDisable"
              ref-type="chat"
              :ref-id="attendance.id"
              :contact-name="contactName"
              :number="attendance"
              @on-message="handleMessage"
              @cancel-reply="handleCancelReply"
              :replying-to="replyingTo"
              @on-command="handleCommand"
              @on-lines="recalcArea"
            />
          </template>
        </div>
      </template>

      <div
        v-if="isDraggingOver && allowAnswer && !isDisable"
        class="text-center dragging-over justify-center"
        :style="`top: ${(topArea - 36)}px; height: ${heightArea}px; width: ${widthArea + 16}px;`"
      >
        <span class="text-light-blue-10">
          Arraste e solte os arquivos aqui!
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { dom } from 'quasar'
import moment from 'moment-timezone'
import AttendanceHeader from './AttendanceHeader'
import AttendanceChatInput from './AttendanceChatInput'
import AttendanceTransferInput from './AttendanceTransferInput'
import AttendanceChatExpired from './AttendanceChatExpired'
import { mapState, mapActions } from 'vuex'
import { getChatExpired, getContactNumber, getMessageStatus, hasContactReplied } from '@/libs/helpers'
import AttendanceChatMessage from '@/components/AttendanceChatMessage'
import AttendanceChatNote from '@/components/AttendanceChatNote'
import { AI_NOTE_TYPE, interleaveAgentNotes } from '@/libs/agentNotes'
import BarAttendanceFrequency from './BarAttendanceFrequency'
import ContactAttendancesHistory from './ContactAttendancesHistory'
import ScrollToBottomButton from './ScrollToBottomButton'

export default {
  components: {
    AttendanceHeader,
    AttendanceChatInput,
    AttendanceChatMessage,
    AttendanceChatNote,
    AttendanceChatExpired,
    BarAttendanceFrequency,
    AttendanceTransferInput,
    ContactAttendancesHistory,
    ScrollToBottomButton
  },

  props: {
    tab: {},
    attendanceId: {}
  },

  data () {
    return {
      aiNoteType: AI_NOTE_TYPE,
      messages: [],
      isDraggingOver: false,

      topArea: 0,
      heightArea: 0,
      widthArea: 0,
      displayArea: 'block',
      destination: null,
      replyingTo: null,
      highlightedMessageId: null,
      isLoadingMessages: false,
      isScrollingToParent: false
    }
  },

  computed: {
    isLoading () {
      return !this.messages.length
    },

    ...mapState({
      agent: ({ agentModule }) => agentModule?.session?.agent ?? {},
      isPaused: (_, getters) => getters['agentModule/isPauseLocked'],

      countAttachments ({ uploadModule }) {
        const files = Array.isArray(uploadModule.files) ? [...uploadModule.files] : []
        return files.filter((attach) => attach.refId === this.attendance.id && attach.refType === 'chat').length
      },

      connected: ({ socketModule }) => socketModule.connected,

      attendance ({ attendanceModule }) {
        const { current, history } = attendanceModule
        const attendance = current[this.attendanceRef] || history[this.attendanceRef] || {}
        if (!Array.isArray(attendance.messages)) attendance.messages = []
        return attendance
      },

      attendanceRef () {
        return Number(this.attendanceId)
      },

      isDisable () {
        const vm = this
        if (vm.isPaused) return true
        if (vm.attendance.finishedAt) return true
        if (!vm.attendance.answeredAt) {
          const isWhatsappapiOut = vm.attendance.source === 'WHATSAPPAPI' && vm.attendance.direction === 'OUT'
          return !(isWhatsappapiOut && vm.whatsappapiAllowFollowup)
        }
        if (vm.attendance.source === 'WHATSAPPAPI' && vm.hasContactReplied) return false
        if (isValidAttendance(vm.attendance)) return false

        const hoursAnswered = Number(moment().diff(moment(vm.attendance.answeredAt), 'hours'))
        if (hoursAnswered < 24) return false

        let isAnswered = false
        const { messages, createdAt: startChat, parentId, lastMessageIn, id, direction } = vm.attendance
        const attendanceIsOut = direction === 'OUT'

        const createdAt = messages.reduce((max, msg) => {
          const msgIsIn = msg.direction === 'IN'
          const createdAt = Math.floor(msg.createdAt / 1000)

          if (attendanceIsOut && msgIsIn && msg.chatId === id) isAnswered = true
          if (!msgIsIn) return max
          return createdAt > max ? createdAt : max
        }, 0)

        const isClonedChatOut = (parentId && Number(lastMessageIn) < Math.floor(startChat / 1000))
        if (isClonedChatOut) return false
        if (!createdAt || (!isAnswered && attendanceIsOut)) return true

        const now = Math.floor(Date.now() / 1000)
        const hours = Math.floor((now - createdAt) / 3600)

        return hours >= 24
      },

      allowAnswer () {
        const vm = this
        const { status } = vm.attendance
        return vm.connected && status === 'ONGOING'
      },

      expiredAttendance () {
        if (this.attendance.source === 'WHATSAPPAPI' && this.hasContactReplied) return false
        return getChatExpired(this.attendance)
      },

      contactName () {
        return this.attendance.contact
      },

      isWhatsApp () {
        return ['WHATSAPP', 'WHATSAPPAPI'].includes(this.attendance.source)
      },

      canCloseAttendance ({ agentModule }) {
        return agentModule?.session?.configs?.canCloseAttendance === 'yes'
      },

      whatsappapiAllowFollowup ({ agentModule }) {
        const value = String(agentModule?.session?.configs?.whatsappapiAllowFollowup).trim().toLowerCase()
        return ['true', 'yes', '1'].includes(value)
      }
    }),

    contactNumber () {
      return getContactNumber(this.attendance)
    },

    hasContactReplied () {
      return hasContactReplied(this.attendance)
    },

    isFinishDisabled () {
      if (this.canCloseAttendance) return false
      if (this.attendance.source === 'WHATSAPPAPI') return false
      return !this.hasContactReplied
    },

    membership () {
      return Array.isArray(this.agent.membership) ? [...this.agent.membership] : []
    },

    queueId () {
      if (!this.agent.id) return this.attendance?.queue
      return this.attendance.queue || this.attendance.queueId
    },

    queueName () {
      const queueDirection = this.queueId
      if (!queueDirection) return null

      const queue = this.membership.find(({ queueId }) => queueId === queueDirection)
      return queue?.queueName ?? queueDirection
    },

    allMessages () {
      const vm = this

      const attendanceId = vm.attendance?.id ?? vm.attendance?.temporaryId
      if (!attendanceId) return []

      const messages = Array.isArray(vm.attendance?.messages) ? vm.attendance.messages : []
      const messagesWithStatus = messages.map((msg) => Object.assign({}, msg, getMessageStatus(msg)))

      const reactions = messagesWithStatus.filter(m => m.type === 'REACTION')

      for (const reaction of reactions) {
        try {
          const reactionData = JSON.parse(reaction.message)
          const originalMessage = messagesWithStatus.find(m => m.ref === reactionData.message_id)

          if (originalMessage) {
            originalMessage.reaction = { emoji: reactionData.emoji }
          }
        } catch (e) { console.error('Failed to parse reaction message', e) }
      }

      const timeline = messagesWithStatus.map(msg => {
        if (msg.parentRef) {
          if (typeof msg.parentMessage === 'string') {
            msg.parentMessage = {
              message: msg.parentMessage,
              direction: msg.parentDirection,
              type: msg.parentType
            }
          } else {
            const parentMessage = messagesWithStatus.find(p => p.ref === msg.parentRef)
            if (parentMessage) {
              msg.parentMessage = parentMessage
            }
          }
        }
        return msg
      })

      return interleaveAgentNotes(timeline, vm.attendance?.notes)
    },

    isOngoingTab () {
      return this.tab === 'current'
    },

    isHistoryTab () {
      return this.tab === 'history'
    },

    attendanceFrequency () {
      return this.attendance?.attendanceFrequency
    }
  },

  mounted () {
    const vm = this
    vm.isMounted = true
    vm.recalcArea()
    vm.$store.dispatch('quickAnswersModule/updateQueueAttendance', vm.attendance.queueId)
    vm.focusInput()
    window.addEventListener('resize', vm.recalcArea)

    vm.$nextTick().then(() => {
      if (!vm.isMounted) return
      if (!vm.allMessages.length) {
        if (vm.attendance?.loadedAsEmpty) vm.loadPreviousMessages()
        return
      }
      vm.messages = vm.allMessages

      vm.fetchMessagesUntilScrollable()
      vm.checkVirtualScroll()
    })
  },

  beforeCreate () {
    const vm = this
    vm.isMounted = false
    window.removeEventListener('resize', vm.recalcArea)
  },

  updated () {
    if (!this.destination) this.focusInput()
  },

  methods: {
    ...mapActions({
      setReaded: 'socketModule/setReaded',
      sendMessage: 'socketModule/sendMessage',
      startUpload: 'uploadModule/startUpload',
      setFinished: 'socketModule/setFinished',
      sendTransfer: 'socketModule/sendTransfer',
      loadMessages: 'attendanceModule/loadMessagesChat'
    }),

    focusInput () {
      this.$nextTick(() => {
        this.$refs.chatInput?.focus()
      })
    },

    async fetchMessagesUntilScrollable () {
      if (!this.isMounted) return
      await this.$nextTick()

      const scrollEl = this.$refs.scrollmsgs?.$el
      if (!scrollEl || !this.attendance.hasMore) return

      const isScrollable = scrollEl.scrollHeight > scrollEl.clientHeight
      if (!isScrollable) {
        const currentMessageCount = this.messages.length
        await this.loadPreviousMessages()

        if (this.messages.length > currentMessageCount) {
          await this.fetchMessagesUntilScrollable()
        }
      }
    },

    async checkVirtualScroll () {
      await wait(500)
      if (this.$refs.scrollmsgs) {
        const prevToIndex = this.$refs.scrollmsgs.prevToIndex
        const fixScroll = prevToIndex === -1 && this.messages.length > 0
        if (fixScroll) this.$refs.scrollmsgs.refresh()
      }
    },

    recalcArea () {
      const vm = this
      if (!vm.isOngoingTab) return
      if (!vm.$refs.container) return
      if (vm.displayArea === 'none') return

      vm.displayArea = 'none'

      vm.$nextTick().then(() => {
        try {
          const widthArea = dom.width(vm.$refs.container)
          const heightArea = dom.height(vm.$refs.container)
          const canUpdate = widthArea !== vm.widthArea || heightArea !== vm.heightArea

          if (canUpdate) {
            vm.topArea = dom.offset(vm.$refs.container).top
            vm.widthArea = widthArea
            vm.heightArea = heightArea
          }
        } finally {
          vm.displayArea = 'block'
        }
      })
    },

    async onScroll (props) {
      if (this.$refs.scrollBtn) {
        this.$refs.scrollBtn.updateVisibility(props)
      }

      if (props.direction !== 'decrease') return
      if (props.index > 0) return
      if (this.isLoadingMessages) return

      await this.loadPreviousMessages()
    },

    async loadPreviousMessages () {
      this.isLoadingMessages = true
      try {
        await this.loadMessages({
          ...this.attendance,
          createdAt: null,
          answeredAt: null
        })
      } finally {
        this.isLoadingMessages = false
      }
    },

    handleMessage (msg) {
      this.sendMessage({
        ...msg,
        attendanceId: this.attendanceRef,
        parentMessage: this.replyingTo
      })

      if (this.replyingTo) {
        this.replyingTo = null
      }

      setTimeout(() => {
        this.scrollToBottom()
      }, 250)
    },

    handleCancelReply () {
      this.replyingTo = null
    },

    scrollToBottom () {
      this.$refs.scrollmsgs.scrollTo(this.messages.length - 1, 'start')
      if (this.$refs.scrollBtn) {
        this.$refs.scrollBtn.hide()
      }
    },

    handleReply (msg) {
      this.replyingTo = msg
      this.focusInput()
    },

    async handleScrollToParent (parentRef) {
      if (!parentRef) return

      this.isScrollingToParent = true

      const scrollToMessage = () => {
        const message = this.messages.find(m => m.ref === parentRef)

        if (message) {
          const index = this.messages.indexOf(message)
          this.$refs.scrollmsgs.scrollTo(index, 'center')
          this.highlightedMessageId = message.id || message.temporaryId
          setTimeout(() => {
            this.highlightedMessageId = null
          }, 2000)
          return true
        }
        return false
      }

      if (scrollToMessage()) {
        this.isScrollingToParent = false
        return
      }

      try {
        this.isLoadingMessages = true

        while (this.attendance.hasMore) {
          const currentMessageCount = this.messages.length
          await this.loadMessages(this.attendance)
          await this.$nextTick()
          if (this.messages.length === currentMessageCount) {
            break
          }
          if (scrollToMessage()) return
        }
      } finally {
        this.isScrollingToParent = false
        this.isLoadingMessages = false
      }
    },

    handleCommand ({ destination }) {
      console.log('HANDLE COMMAND', destination)
      this.destination = destination
    },

    handleVisible (message) {
      if (!this.allowAnswer) return
      const messageId = message.id
      const vm = this
      if (message.direction !== 'IN') return
      vm.setReaded({ attendanceId: vm.attendanceRef, messageId })
    },

    handleFinish () {
      this.setFinished(this.attendanceRef)
    },

    handleDragOver (event) {
      event.preventDefault()
    },

    handleDrop (event) {
      event.preventDefault()
      this.isDraggingOver = false
      if (this.isDisable) return
      this.startUpload({
        refId: this.attendance.id,
        refType: 'chat',
        files: event.dataTransfer.files
      })
    },

    handleDragEnter (event) {
      event.preventDefault()
      if (this.isDraggingOver) return
      this.isDraggingOver = true
    },

    handleDragLeave (event) {
      event.preventDefault()
      if (!this.isDraggingOver) return
      if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
        this.isDraggingOver = false
      }
    }
  },

  watch: {
    allowAnswer () {
      this.$nextTick(this.recalcArea)
    },

    isLoading () {
      this.$nextTick(this.recalcArea)
    },

    expiredAttendance () {
      this.$nextTick(this.recalcArea)
    },

    replyingTo: {
      deep: true,
      handler (newValue, oldValue) {
        if (newValue === oldValue) return
        this.$nextTick(this.recalcArea)
      }
    },

    countAttachments: {
      deep: true,
      handler (newCount, oldCount) {
        if (newCount === oldCount) return
        this.recalcArea()
      }
    },

    allMessages: {
      deep: true,
      handler (newMessages, oldMessages) {
        const vm = this
        if (isEqual(newMessages, oldMessages)) return
        const onlyUpdateStatus = newMessages?.length === oldMessages?.length

        if (onlyUpdateStatus || vm.isScrollingToParent) {
          vm.messages = newMessages
          return
        }

        if (!oldMessages?.length) {
          vm.messages = newMessages
          vm.$nextTick().then(() => {
            vm.fetchMessagesUntilScrollable()
            vm.checkVirtualScroll()
          })
          return
        }

        if (!vm.$refs.scrollmsgs) return
        const prevToIndex = vm.$refs.scrollmsgs.prevToIndex
        const prevMessage = oldMessages.at(prevToIndex)
        const isEnded = prevToIndex === (oldMessages?.length || 0) - 1
        vm.messages = newMessages

        vm.$nextTick().then(async () => {
          const scrollIndex = vm.messages.findIndex((msg) => msg.id === prevMessage.id)

          vm.fetchMessagesUntilScrollable()
          if (isEnded) {
            setTimeout(() => {
              vm.scrollToBottom()
            }, 250)
          } else {
            vm.$refs.scrollmsgs.refresh(scrollIndex)
          }
        })
      }
    },

    messages: {
      deep: true,
      async handler (newMessages, oldMessages) {
        const setEndScroll = Boolean(!oldMessages.length && newMessages.length)
        if (!setEndScroll) return

        const vm = this

        while (true) {
          if (vm.$refs.scrollmsgs) {
            setTimeout(() => {
              vm.scrollToBottom()
              if (vm.$refs.scrollBtn) vm.$refs.scrollBtn.hide()
            }, 250)
            break
          }

          if (!vm.isMounted) {
            break
          }

          await new Promise((resolve) => setTimeout(resolve, 150))
        }
      }
    }
  }
}

const isValidAttendance = ({ createdAt, direction }) => {
  const now = Math.floor(Date.now() / 1000)
  const created = Math.floor(createdAt / 1000)
  const hours = Math.floor((now - created) / 3600)
  const isIn = direction === 'IN'
  return hours < 24 && isIn
}

const isEqual = (source, target) => JSON.stringify(source) === JSON.stringify(target)
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

</script>
<style lang="sass">
.dragging-over
  border: 3.5px dashed rgba(38, 50, 56, 0.5)
  background: #FAFAFA
  position: absolute
  top: 0
  right: 0
  background-color: rgba(255, 255, 255, 0.8)
  display: flex
  align-items: center
  justify-content: center
  z-index: 9999
</style>
